import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import SiteNav from "../components/Layout/SiteNav";
import useHalalFeature from "../hooks/useHalalFeature";
import {
  BRAND,
  EDITORIAL_FONT,
  PatternTile,
  Eyebrow,
  EditorialShell,
  EditorialCard,
  SerifHeading,
  PillButton,
  useEditorialPalette,
} from "../components/editorial/EditorialPrimitives";

const HERO_CHIPS = ["Certified Listings", "In-App Discovery", "Ad-Free, Always"];

const COMMUNITY_FEATURES = [
  {
    t: "Discover Nearby",
    sub: "Every halal spot, mapped",
    b: "Browse restaurants, butchers, grocers, and cafés pinned to your exact location. Filter by cuisine, open now, and distance.",
    ar: "قريب",
  },
  {
    t: "Certified Halal",
    sub: "Every listing is vetted",
    b: "Businesses upload halal certification during signup and are manually reviewed before going live. No guesswork, no surprises.",
    ar: "حلال",
  },
  {
    t: "Menus & Video",
    sub: "See it before you go",
    b: "Pro-tier listings feature full menus, owner posts, and short video showcases so you know exactly what's on the table before you arrive.",
    ar: "قائمة",
  },
  {
    t: "Support Muslim-Owned",
    sub: "Strengthen the community",
    b: "Every discovery directly supports independent Muslim entrepreneurs. Follow favorites, share spots with friends, and build the ecosystem.",
    ar: "مجتمع",
  },
];

const PARTNER_POINTS = [
  {
    t: "Zero acquisition noise",
    b: "Every listing reaches a 100% halal-seeking audience.",
  },
  {
    t: "Mobile-first showcase",
    b: "Menu, videos, location, hours, all native in the app.",
  },
  {
    t: "Featured placements",
    b: "Pro-tier partners surface first in discovery feeds.",
  },
  {
    t: "Trusted halal badge",
    b: "Your certification becomes a conversion signal.",
  },
];

const TRUST_STATS = [
  { n: "100%", t: "Halal-Seeking Users" },
  { n: "Manual", t: "Certification Review" },
  { n: "Ad Free", t: "Always & Forever" },
];

export default function HalalInfo() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const palette = useEditorialPalette();
  const { halalEnabled, loading: halalFlagLoading } = useHalalFeature();

  const isPendingMasjidAdmin =
    user &&
    userProfile?.role === "masjid" &&
    (userProfile?.pendingMasjidApproval === true ||
      !userProfile?.isAdmin ||
      (!userProfile?.masjidId && !userProfile?.activeMasjidId));

  const isIncompleteHalalUser =
    user &&
    userProfile?.role === "halal" &&
    userProfile?.restaurantProfileComplete === false;

  const hasDashboardAccess =
    !isPendingMasjidAdmin &&
    !isIncompleteHalalUser &&
    (userProfile?.role === "super" ||
      userProfile?.role === "halal" ||
      userProfile?.isAdmin);

  const getDashboardRoute = () => {
    if (!user) return "/login";
    if (isPendingMasjidAdmin) return "/masjid-signup";
    if (isIncompleteHalalUser) return "/halal-signup";
    if (userProfile?.role === "super") return "/admin";
    if (userProfile?.role === "halal") return "/halal";
    if (userProfile?.isAdmin) return "/masjid-dashboard";
    return null;
  };

  const goToSignup = () => navigate("/signup");

  // The nav link is hidden while halal is off, but the URL is still reachable,
  // so stand in a coming soon panel instead of the marketing page.
  if (!halalFlagLoading && !halalEnabled) {
    return (
      <EditorialShell>
        <SiteNav />
        <section
          className="relative overflow-hidden"
          style={{ background: palette.ivory }}
        >
          <PatternTile opacity={0.04} />
          <div
            className="relative mx-auto flex min-h-[70vh] flex-col items-center justify-center px-4 py-24 text-center sm:px-6 lg:px-10"
            style={{ maxWidth: 720 }}
          >
            <div className="mb-6 inline-flex">
              <Eyebrow>Coming soon</Eyebrow>
            </div>
            <SerifHeading
              level={1}
              className="mb-6"
              style={{
                fontSize: "clamp(2.25rem, 6vw, 4rem)",
                lineHeight: 1.06,
                letterSpacing: "-0.03em",
              }}
            >
              Halal is on its way.
            </SerifHeading>
            <p
              className="mb-9 text-base sm:text-lg"
              style={{ color: palette.ink2, lineHeight: 1.55 }}
            >
              Certified halal discovery is not available yet. Everything else in
              Muslim+ is ready for you today.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <PillButton variant="primary" onClick={() => navigate("/")}>
                Back to home
              </PillButton>
              <PillButton variant="ghost" onClick={() => navigate("/masjids")}>
                Browse masajid
              </PillButton>
            </div>
          </div>
        </section>
      </EditorialShell>
    );
  }

  return (
    <EditorialShell>
      <SiteNav />

      {/* ══════════════════════════════════
          HERO
          ══════════════════════════════════ */}
      <section
        className="relative overflow-hidden"
        style={{ background: palette.ivory, borderBottom: `1px solid ${palette.hairline}` }}
      >
        <PatternTile opacity={0.04} />
        <div
          className="relative mx-auto px-4 pt-16 pb-16 text-center sm:px-6 sm:pt-24 sm:pb-20 lg:px-10"
          style={{ maxWidth: 1080 }}
        >
          <div className="mb-6 inline-flex">
            <Eyebrow>The halal marketplace</Eyebrow>
          </div>
          <SerifHeading
            level={1}
            className="mb-7"
            style={{
              fontSize: "clamp(2.75rem, 7.5vw, 7rem)",
              lineHeight: 1.04,
              letterSpacing: "-0.035em",
            }}
          >
            Your community's{" "}
            <em style={{ color: BRAND.gold, fontWeight: 400 }}>halal,</em>
            <br />
            all in one place.
          </SerifHeading>
          <p
            className="mx-auto mb-9 max-w-2xl text-base sm:text-lg md:text-xl"
            style={{ color: palette.ink2, lineHeight: 1.55 }}
          >
            In-app and completely ad-free.
          </p>
          <div className="mb-10 flex flex-col gap-3 sm:flex-row sm:justify-center sm:flex-wrap">
            <PillButton variant="primary" onClick={goToSignup}>
              List your halal business
              <span aria-hidden>→</span>
            </PillButton>
            <PillButton
              asChild
              variant="ghost"
            >
              <a
                href="https://apps.apple.com/us/app/muslim-prayer-companion/id6745054019"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get the app
              </a>
            </PillButton>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3">
            {HERO_CHIPS.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[12px] sm:text-[13px]"
                style={{
                  background: "transparent",
                  border: `1px solid ${palette.hairline}`,
                  color: palette.ink2,
                  fontFamily: EDITORIAL_FONT.sans,
                  fontWeight: 500,
                  letterSpacing: "0.02em",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: BRAND.gold,
                  }}
                />
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          FOR THE COMMUNITY
          ══════════════════════════════════ */}
      <section style={{ background: palette.paper }}>
        <div
          className="mx-auto px-4 py-20 sm:px-6 sm:py-24 lg:px-10"
          style={{ maxWidth: 1320 }}
        >
          <div className="mb-12 grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-center lg:gap-16">
            <div>
              <Eyebrow>For the community</Eyebrow>
              <SerifHeading
                className="mt-5"
                style={{
                  fontSize: "clamp(2rem, 4.5vw, 4rem)",
                  lineHeight: 1,
                  letterSpacing: "-0.03em",
                }}
              >
                Know what's{" "}
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>halal</em>{" "}
                before you go.
              </SerifHeading>
            </div>
            <p
              className="max-w-xl text-base sm:text-lg"
              style={{ color: palette.ink2, lineHeight: 1.55 }}
            >
              From weekend brunch to late-night cravings, find halal spots near you, see live
              menus, and know exactly what you're walking into.
            </p>
          </div>

          <div
            className="grid grid-cols-1 border-t md:grid-cols-2"
            style={{ borderColor: palette.hairline }}
          >
            {COMMUNITY_FEATURES.map((f, i) => (
              <div
                key={f.t}
                className="relative px-7 py-10"
                style={{
                  borderRight: i % 2 === 0 ? `1px solid ${palette.hairline}` : "none",
                  borderBottom: `1px solid ${palette.hairline}`,
                }}
              >
                <div className="mb-5 flex items-center justify-between">
                  <div
                    className="text-[11px] font-semibold uppercase"
                    style={{
                      letterSpacing: "0.16em",
                      color: BRAND.gold,
                      fontFamily: EDITORIAL_FONT.sans,
                    }}
                  >
                    {f.sub}
                  </div>
                  <div
                    style={{
                      fontFamily: EDITORIAL_FONT.arabic,
                      fontSize: 22,
                      color: BRAND.gold,
                      direction: "rtl",
                    }}
                  >
                    {f.ar}
                  </div>
                </div>
                <SerifHeading
                  level={3}
                  className="mb-3"
                  style={{ fontSize: 28, fontWeight: 500, lineHeight: 1.1 }}
                >
                  {f.t}
                </SerifHeading>
                <p className="text-sm" style={{ color: palette.ink2, lineHeight: 1.6 }}>
                  {f.b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          FOR HALAL PARTNERS
          ══════════════════════════════════ */}
      <section style={{ background: palette.ivory }}>
        <div
          className="mx-auto px-4 py-20 sm:px-6 sm:py-24 lg:px-10"
          style={{ maxWidth: 1080 }}
        >
          <div>
            <Eyebrow>For halal partners</Eyebrow>
            <SerifHeading
              className="mt-5 mb-6"
              style={{
                fontSize: "clamp(2rem, 4.5vw, 4rem)",
                lineHeight: 1,
                letterSpacing: "-0.03em",
              }}
            >
              Meet the diners{" "}
              <em style={{ color: BRAND.gold, fontWeight: 400 }}>
                already looking for you.
              </em>
            </SerifHeading>
            <p
              className="mb-9 max-w-xl text-[17px]"
              style={{ color: palette.ink2, lineHeight: 1.6 }}
            >
              Muslim+ is built for a high-intent audience. Every user opening the app is actively
              searching for halal. Skip the noise of general platforms and put your business
              directly in front of people ready to walk through your door.
            </p>

            <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2">
              {PARTNER_POINTS.map((p) => (
                <div key={p.t} className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="mt-1.5 flex-shrink-0"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: BRAND.gold,
                    }}
                  />
                  <div>
                    <div
                      className="mb-1"
                      style={{
                        fontFamily: EDITORIAL_FONT.serif,
                        fontSize: 18,
                        fontWeight: 500,
                        letterSpacing: "-0.01em",
                        color: palette.ink,
                      }}
                    >
                      {p.t}
                    </div>
                    <p
                      className="text-[14px]"
                      style={{ color: palette.ink2, lineHeight: 1.5 }}
                    >
                      {p.b}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <PillButton variant="primary" onClick={goToSignup}>
              Start your listing
              <span aria-hidden>→</span>
            </PillButton>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          BUILT ON TRUST
          ══════════════════════════════════ */}
      <section style={{ background: palette.bandBg, color: palette.bandText }}>
        <PatternTile opacity={0.04} color={BRAND.gold} />
        <div
          className="relative mx-auto px-4 py-20 sm:px-6 sm:py-24 lg:px-10"
          style={{ maxWidth: 1080 }}
        >
          <div className="text-center">
            <Eyebrow color={BRAND.gold}>The promise</Eyebrow>
            <SerifHeading
              className="mt-5 mb-6"
              style={{
                fontSize: "clamp(2rem, 5vw, 4.5rem)",
                lineHeight: 1.02,
                letterSpacing: "-0.03em",
              }}
            >
              Built on trust.
              <br />
              <em style={{ color: BRAND.gold, fontWeight: 400 }}>
                Powered by community.
              </em>
            </SerifHeading>
            <p
              className="mx-auto max-w-2xl text-base sm:text-lg"
              style={{ color: "#c8bfa8", lineHeight: 1.6 }}
            >
              Muslim+ exists to serve Muslim families first. That means real verification, no paid
              positioning hacks, and an ecosystem where halal partners and eaters both thrive.
            </p>
          </div>

          <div
            className="mt-14 grid grid-cols-1 gap-px md:grid-cols-3"
            style={{ background: "#2a2520" }}
          >
            {TRUST_STATS.map((s) => (
              <div
                key={s.t}
                className="px-6 py-10 text-center"
                style={{ background: BRAND.ink }}
              >
                <div
                  className="mb-3"
                  style={{
                    fontFamily: EDITORIAL_FONT.serif,
                    fontSize: "clamp(2.5rem, 4vw, 3.75rem)",
                    fontWeight: 500,
                    letterSpacing: "-0.03em",
                    lineHeight: 1,
                    color: BRAND.gold,
                  }}
                >
                  {s.n}
                </div>
                <div
                  className="text-[11px] font-semibold uppercase"
                  style={{ letterSpacing: "0.16em", color: "#c8bfa8" }}
                >
                  {s.t}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          CTA — READY TO LIST
          ══════════════════════════════════ */}
      <section
        style={{ background: palette.ivory, borderTop: `1px solid ${palette.hairline}` }}
      >
        <div
          className="mx-auto px-4 py-20 text-center sm:px-6 sm:py-24 lg:px-10"
          style={{ maxWidth: 1080 }}
        >
          <SerifHeading
            className="mb-6"
            style={{
              fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)",
              lineHeight: 0.98,
            }}
          >
            Ready to <em style={{ color: BRAND.gold, fontWeight: 400 }}>list?</em>
          </SerifHeading>
          <p className="mx-auto mb-8 max-w-xl text-[17px]" style={{ color: palette.ink2 }}>
            Create your Muslim+ account, upload your halal certification, and go live in the app,
            all in under ten minutes.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <PillButton variant="primary" onClick={goToSignup}>
              Continue to signup
              <span aria-hidden>→</span>
            </PillButton>
            <PillButton variant="ghost" onClick={() => navigate("/login")}>
              Already a partner? Sign in
            </PillButton>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          FOOTER
          ══════════════════════════════════ */}
      <footer
        className="relative"
        style={{
          background: palette.paper,
          borderTop: `1px solid ${palette.hairline}`,
          color: palette.ink,
        }}
      >
        <div className="container mx-auto px-4 py-16 sm:px-6 lg:px-10">
          <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
            <div className="lg:col-span-2">
              <div className="mb-4 flex items-center gap-2.5">
                <span
                  style={{
                    fontFamily: EDITORIAL_FONT.serif,
                    fontSize: 24,
                    fontWeight: 600,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Muslim<span style={{ color: BRAND.gold }}>+</span>
                </span>
              </div>
              <p
                className="mb-6 max-w-md text-[15px] leading-relaxed"
                style={{ color: palette.muted }}
              >
                Connecting Muslims with their community through technology and faith. Find prayer
                times, discover nearby masajid, stay updated with events, and explore halal
                restaurants.
              </p>
              <div className="flex flex-wrap gap-2">
                {["Trusted Platform", "Verified Content", "Real-time Updates"].map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium"
                    style={{
                      background: "transparent",
                      border: `1px solid ${palette.hairline}`,
                      color: palette.muted,
                      letterSpacing: "0.04em",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <div
                className="mb-5 text-[11px] font-semibold uppercase"
                style={{ letterSpacing: "0.16em", color: palette.muted }}
              >
                Quick links
              </div>
              <div className="flex flex-col gap-3 text-sm">
                <button
                  onClick={() => navigate("/masjids")}
                  className="text-left transition-colors hover:text-[#bf9a2f]"
                >
                  Find Masajid
                </button>
                <button
                  onClick={() => navigate("/events-public")}
                  className="text-left transition-colors hover:text-[#bf9a2f]"
                >
                  Browse Events
                </button>
                {user && userProfile ? (
                  <button
                    onClick={() => {
                      if (isPendingMasjidAdmin) return navigate("/masjid-signup");
                      if (isIncompleteHalalUser) return navigate("/halal-signup");
                      const dashboardRoute = getDashboardRoute();
                      navigate(dashboardRoute || "/settings");
                    }}
                    className="text-left transition-colors hover:text-[#bf9a2f]"
                  >
                    {isPendingMasjidAdmin || isIncompleteHalalUser
                      ? "Complete Signup"
                      : hasDashboardAccess
                      ? "Dashboard"
                      : "Settings"}
                  </button>
                ) : (
                  <button
                    onClick={() => navigate("/login")}
                    className="text-left transition-colors hover:text-[#bf9a2f]"
                  >
                    Sign In
                  </button>
                )}
                <button
                  onClick={() => navigate("/policy")}
                  className="text-left transition-colors hover:text-[#bf9a2f]"
                >
                  Privacy Policy
                </button>
              </div>
            </div>

            <div>
              <div
                className="mb-5 text-[11px] font-semibold uppercase"
                style={{ letterSpacing: "0.16em", color: palette.muted }}
              >
                Resources
              </div>
              <div className="flex flex-col gap-3 text-sm">
                <a
                  href="https://apps.apple.com/us/app/muslim-prayer-companion/id6745054019"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-[#bf9a2f]"
                >
                  Download iOS App
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.psu.pdxmsa&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-[#bf9a2f]"
                >
                  Download Android App
                </a>
                <a
                  href="https://www.instagram.com/muslimplus.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-[#bf9a2f]"
                >
                  Follow us on Instagram
                </a>
              </div>
            </div>
          </div>

          <div
            className="mt-12 flex flex-col items-start justify-between gap-3 border-t pt-8 text-xs md:flex-row md:items-center"
            style={{ borderColor: palette.hairline, color: palette.muted }}
          >
            <span>© {new Date().getFullYear()} Muslim+. All rights reserved.</span>
            <span>Made with ❤️ for the Muslim community</span>
          </div>
        </div>
      </footer>
    </EditorialShell>
  );
}
